import { Button, Col, Modal, Row } from 'reactstrap';
import { useEffect, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { drawSignatureImage } from '../../../../helpers/embedSignatureImage';
import moment from 'moment';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type DocumentModalProps = {
  isOpen: boolean;
  data: any;
  toggleModal: (e?: any) => void;
};

const DocumentModal = ({ isOpen, data, toggleModal }: DocumentModalProps) => {
  const [pdf, setPdf] = useState('');

  useEffect(() => {
    if (isOpen && data) {
      loadPdf();
    }
  }, [isOpen, data]);

  const fetchPdfBytes = async (): Promise<ArrayBuffer | null> => {
    const candidates: string[] = [];
    const push = (raw: any) => {
      if (typeof raw !== 'string') return;
      const trimmed = raw.trim();
      if (!trimmed) return;
      candidates.push(/^(https?|data):/i.test(trimmed) ? trimmed : `data:application/pdf;base64,${trimmed}`);
    };
    push(data?.uploadedPdf);
    push(data?.options?.file);
    push(data?.options?.url);
    push(data?.pdfUrl);
    push(data?.file);

    for (const url of candidates) {
      try {
        const buf = await fetch(url).then((res) => res.arrayBuffer());
        if (buf.byteLength >= 4) {
          const head = new Uint8Array(buf, 0, 4);
          if (head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46) {
            return buf;
          }
        }
      } catch {
        // try next candidate
      }
    }
    return null;
  };

  const loadPdf = async () => {
    try {
      const arrayBuffer = await fetchPdfBytes();
      if (!arrayBuffer) {
        console.error('No valid PDF could be loaded from data object', data);
        return;
      }
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaObliqueFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      const pages = pdfDoc.getPages();
      const config = data.options || {};
      const signatureImageBase64 = data?.options?.signPng || data?.signature;

      let signaturePage;

      if (config.signPage === -1) {
        signaturePage = pdfDoc.addPage();
        pages.push(signaturePage);
        const { width } = signaturePage.getSize();
        const employeeName = data?.userName || '';
        const documentTitle = config.title || 'the document';

        let acknowledgementText = config.acknowledgementText.text;
        acknowledgementText = acknowledgementText.replace('{employeeName}', employeeName);
        acknowledgementText = acknowledgementText.replace('{documentTitle}', documentTitle);

        signaturePage.drawText(acknowledgementText, {
          ...config.acknowledgementText,
          font: helveticaFont,
          maxWidth: width - config.acknowledgementText.x * 2,
          lineHeight: 15,
        });

        const lineOptions = { thickness: 0.5, color: rgb(0.2, 0.2, 0.2) };
        const labelOptions = { font: helveticaObliqueFont, size: 9, color: rgb(0.3, 0.3, 0.3) };

        signaturePage.drawText(config.employeeBlock.label.text, { ...config.employeeBlock.label, ...labelOptions });
        signaturePage.drawLine({ ...config.employeeBlock.signature.line, ...lineOptions });
        signaturePage.drawText(config.employeeBlock.printedName.label.text, {
          ...config.employeeBlock.printedName.label,
          ...labelOptions,
        });
        signaturePage.drawLine({ ...config.employeeBlock.printedName.line, ...lineOptions });
        signaturePage.drawText(config.employeeBlock.date.label.text, {
          ...config.employeeBlock.date.label,
          ...labelOptions,
        });
        signaturePage.drawLine({ ...config.employeeBlock.date.line, ...lineOptions });
      } else {
        signaturePage = pages[config.signPage || 0];
      }

      if (signaturePage && signatureImageBase64) {
        const staffName = data?.userName || '';
        const signatureDate = moment(config.signatureDate).format('MM/DD/YYYY');

        const pngImage = await pdfDoc.embedPng(signatureImageBase64);
        const signatureCoords =
          config.employeeBlock?.signature?.coordinates || config.signature?.coordinates || config.coordinates;
        drawSignatureImage(signaturePage, pngImage, signatureCoords);

        if (config.signPage === -1) {
          if (config.employeeBlock?.printedName?.coordinates) {
            signaturePage.drawText(staffName, {
              ...config.employeeBlock.printedName.coordinates,
              font: timesRomanFont,
              color: rgb(0, 0, 0),
            });
          }

          if (config.employeeBlock?.date?.coordinates) {
            signaturePage.drawText(signatureDate, {
              ...config.employeeBlock.date.coordinates,
              font: timesRomanFont,
              color: rgb(0, 0, 0),
            });
          }
        } else {
          if (config.coordinatesRectangle) {
            signaturePage.drawRectangle({
              ...config.coordinatesRectangle,
            });
          }

          const nameConfig = config.coordinatesName;
          if (nameConfig) {
            const namePage = pages[config.signNamePage || config.signPage || 0];
            namePage.drawText(staffName, { ...nameConfig, font: timesRomanFont, color: rgb(0, 0, 0) });
          }

          const dateConfig = config.coordinatesDate;
          if (dateConfig) {
            const datePage = pages[config.signDatePage || config.signPage || 0];
            datePage.drawText(signatureDate, { ...dateConfig, font: timesRomanFont, color: rgb(0, 0, 0) });
          }

          if (config.coordinatesNames) {
            for (const n of config.coordinatesNames) {
              pages[n.page || 0].drawText(staffName, { ...n.coordinates, font: timesRomanFont, color: rgb(0, 0, 0) });
            }
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const docUrl = URL.createObjectURL(blob);
      setPdf(docUrl);
    } catch (e) {
      console.error('Failed to load signed PDF:', e);
    }
  };

  const handleDownloadPdf = () => {
    if (!pdf) {
      return;
    }
    const link = document.createElement('a');
    link.href = pdf;
    const filename = `${data?.options?.title || 'document'}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={toggleModal}>
      <div className="modal-header pb-1">
        <h5 className="modal-title">{data?.options?.title || ''}</h5>
        <button aria-label="Close" className="close" data-dismiss="modal" type="button" onClick={toggleModal}>
          <span aria-hidden={true}>×</span>
        </button>
      </div>
      <div className="modal-body pt-0">
        <Row className="mt-2">
          <Col md="12" lg="12">
            <div className="d-flex justify-content-end">
              <Button color="primary" onClick={handleDownloadPdf} size="sm">
                Download (PDF)
              </Button>
            </div>
          </Col>
          <Col lg="12" className="mt-4" style={{ height: '500px', overflow: 'auto', border: '1px solid #dee2e6' }}>
            {pdf ? (
              <iframe
                src={pdf}
                style={{ minHeight: '100%', width: '100%', border: 'none' }}
                title={data?.options?.title}
              />
            ) : (
              <p>Loading document...</p>
            )}
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default DocumentModal;
